import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { Button } from "../ui/button";
import { EnvelopeSimple, Phone, ChatCircleDots } from "@phosphor-icons/react";

const STATUSES = ["new", "contacted", "converted", "closed"];
const STATUS_STYLES = {
  new: "bg-amber-500/15 text-amber-400 border-amber-500/40",
  contacted: "bg-blue-500/15 text-blue-400 border-blue-500/40",
  converted: "bg-emerald-500/15 text-emerald-400 border-emerald-500/40",
  closed: "bg-zinc-500/15 text-zinc-400 border-zinc-500/40",
};

export default function LeadDetailDrawer({ leadId, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("new");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/admin/leads/${leadId}`).then(({ data }) => {
      setDetail(data);
      setNotes(data.lead.admin_notes || "");
      setStatus(data.lead.status);
    });
  }, [leadId]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/leads/${leadId}`, { status, admin_notes: notes });
      toast.success("Lead updated");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update lead");
    } finally {
      setSaving(false);
    }
  };

  const lead = detail?.lead;

  return (
    <Sheet open onOpenChange={(v) => !v && onClose()}>
      <SheetContent data-testid="lead-detail-drawer" className="bg-[#141414] border-[#27272A] text-white w-full sm:max-w-lg overflow-y-auto">
        {!lead ? (
          <p className="text-[#71717A] mt-10">Loading lead...</p>
        ) : (
          <>
            <SheetHeader>
              <SheetTitle className="text-white font-heading text-2xl">{lead.name}</SheetTitle>
              <p className="text-xs text-[#71717A] capitalize">{lead.type} lead &middot; {new Date(lead.created_at).toLocaleString()}</p>
            </SheetHeader>

            <div className="mt-6 space-y-6">
              <div className="flex flex-wrap gap-3">
                <a data-testid="drawer-email-link" href={`mailto:${lead.email}`} className="flex items-center gap-2 text-sm text-[#D4D4D8] border border-[#27272A] rounded-full px-4 py-2 hover:border-[#D4AF37]/60 hover:text-white transition-colors">
                  <EnvelopeSimple size={16} className="text-[#D4AF37]" /> {lead.email}
                </a>
                {lead.phone && (
                  <a data-testid="drawer-call-link" href={`tel:${lead.phone}`} className="flex items-center gap-2 text-sm text-[#D4D4D8] border border-[#27272A] rounded-full px-4 py-2 hover:border-[#D4AF37]/60 hover:text-white transition-colors">
                    <Phone size={16} className="text-[#D4AF37]" /> {lead.phone}
                  </a>
                )}
              </div>

              <div>
                <label className="text-xs text-[#71717A] mb-1.5 block">Status</label>
                <select
                  data-testid="drawer-status-select"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className={`w-full border rounded-xl px-3 py-2.5 text-sm font-semibold capitalize bg-[#0A0A0A] ${STATUS_STYLES[status]}`}
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s} className="bg-[#141414] text-white">
                      {s}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-3 text-sm">
                {lead.preferred_datetime && (
                  <p data-testid="drawer-requested-time">
                    <span className="text-[#71717A]">Requested Time:</span> <span className="text-[#D4AF37]">{lead.preferred_datetime}</span>
                  </p>
                )}
                {lead.package_name && (
                  <p data-testid="drawer-package-name">
                    <span className="text-[#71717A]">Package Interested:</span> {lead.package_name}
                  </p>
                )}
                {Array.isArray(lead.goals) && lead.goals.length > 0 && (
                  <div data-testid="drawer-goals">
                    <span className="text-[#71717A] block mb-1.5">Goals:</span>
                    <div className="flex flex-wrap gap-1.5">
                      {lead.goals.map((g) => (
                        <span key={g} className="text-xs bg-[#D4AF37]/10 text-[#D4AF37] px-2.5 py-1 rounded-full capitalize">
                          {g.replace(/_/g, " ")}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {lead.experience_level && (
                  <p>
                    <span className="text-[#71717A]">Experience Level:</span> <span className="capitalize">{lead.experience_level}</span>
                  </p>
                )}
                {lead.location_preference && (
                  <p>
                    <span className="text-[#71717A]">Location Preference:</span> <span className="capitalize">{lead.location_preference.replace(/_/g, " ")}</span>
                  </p>
                )}
                {lead.message && (
                  <div data-testid="drawer-full-message">
                    <span className="text-[#71717A] block mb-1.5">Message:</span>
                    <p className="text-[#D4D4D8] whitespace-pre-wrap bg-[#0A0A0A] border border-[#27272A] rounded-xl p-3">{lead.message}</p>
                  </div>
                )}
              </div>

              {detail.transcript.length > 0 && (
                <div>
                  <p className="text-xs text-[#71717A] mb-2 flex items-center gap-1.5">
                    <ChatCircleDots size={14} /> Chatbot Transcript
                  </p>
                  <div data-testid="drawer-chat-transcript" className="space-y-2 max-h-72 overflow-y-auto bg-[#0A0A0A] border border-[#27272A] rounded-xl p-3">
                    {detail.transcript.map((m, i) => (
                      <div
                        key={i}
                        className={`max-w-[85%] px-3 py-2 rounded-xl text-sm ${
                          m.role === "user" ? "bg-[#D4AF37]/10 text-white ml-auto" : "bg-white/5 text-[#D4D4D8]"
                        }`}
                      >
                        {m.content}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="text-xs text-[#71717A] mb-1.5 block">Admin Notes (internal only)</label>
                <textarea
                  data-testid="drawer-admin-notes-input"
                  rows={4}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Log call outcomes, follow-up reminders, etc."
                  className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-xl px-3 py-2.5 text-white text-sm"
                />
              </div>

              <Button data-testid="drawer-save-button" onClick={save} disabled={saving} className="w-full bg-[#D4AF37] text-black hover:bg-white rounded-full">
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
